import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Sunset } from 'lucide-react';
import { SplineScene } from './SplineScene';

interface TimeGreetingProps {
  userName?: string;
}

export const TimeGreeting: React.FC<TimeGreetingProps> = ({ userName = 'Dr.' }) => {
  const [time, setTime] = useState(new Date());
  const [greeting, setGreeting] = useState('');
  const [theme, setTheme] = useState({
    icon: <Sun className="w-8 h-8" />,
    gradient: 'from-amber-500/30 via-orange-500/20 to-transparent',
    iconColor: 'text-amber-400'
  });

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const hour = time.getHours();
    if (hour >= 5 && hour < 12) {
      setGreeting('Bom dia');
      setTheme({
        icon: <Sun className="w-8 h-8" />,
        gradient: 'from-amber-500/30 via-orange-500/20 to-transparent',
        iconColor: 'text-amber-400'
      });
    } else if (hour >= 12 && hour < 16) {
      setGreeting('Boa tarde');
      setTheme({
        icon: <Sunset className="w-8 h-8" />,
        gradient: 'from-orange-500/30 via-pink-500/20 to-transparent',
        iconColor: 'text-orange-400'
      });
    } else if (hour >= 16 && hour < 18) {
      setGreeting('Boa tarde');
      setTheme({
        icon: <Sunset className="w-8 h-8" />,
        gradient: 'from-blue-600/30 via-indigo-600/20 to-transparent',
        iconColor: 'text-blue-400'
      });
    } else {
      setGreeting('Boa noite');
      setTheme({
        icon: <Moon className="w-8 h-8" />,
        gradient: 'from-blue-900/40 via-indigo-900/30 to-transparent',
        iconColor: 'text-blue-400'
      });
    }
  }, [time]);

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).format(date);
  };

  return (
    <div className="relative w-full mb-12 overflow-hidden">
      {/* Gradiente de fundo que invade a página */}
      <div className={`absolute inset-0 bg-gradient-to-b ${theme.gradient}`} />
      
      {/* Círculos de blur */}
      <motion.div 
        className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-gradient-to-r from-white/5 to-transparent blur-3xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      <motion.div 
        className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-gradient-to-l from-white/5 to-transparent blur-3xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
      />

      {/* Content */}
      <div className="relative pt-12 pb-24 flex flex-col items-center justify-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
          className="mb-6"
        >
          <div className="flex flex-col items-center gap-2">
            <div className={`mb-4 ${theme.iconColor}`}>
              {theme.icon}
            </div>
            <h1 className="text-[72px] font-semibold text-white tracking-tight">
              {formatTime(time)}
            </h1>
            <p className="text-[20px] text-[rgb(152,152,157)] capitalize">
              {formatDate(time)}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
        >
          <h2 className="text-[28px] font-semibold text-white">
            {greeting}, <span className="text-white/80">{userName}</span>
          </h2>
        </motion.div>
      </div>
    </div>
  );
};
